import { useState, useEffect, useCallback, useRef } from 'react'
import { Send, ChevronDown, Printer, RefreshCw, Download } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'
import { downloadPdf } from '../../utils/pdfUtils'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

function groupByVendedor(parcelas) {
  const map = {}
  for (const p of parcelas) {
    const nome = p.vendedor?.nome || 'Sem Vendedor'
    if (!map[nome]) map[nome] = []
    map[nome].push(p)
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
}

function SecaoStatus({ titulo, cor, parcelas, colunaExtra }) {
  const total = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const grupos = groupByVendedor(parcelas)

  const corMap = {
    amarelo: { header: 'bg-yellow-100', headerTxt: 'text-yellow-800', border: 'border-yellow-200', foot: 'bg-yellow-50', footTxt: 'text-yellow-800', rowAlt: 'bg-yellow-50/20' },
    verde: { header: 'bg-green-100', headerTxt: 'text-green-800', border: 'border-green-200', foot: 'bg-green-50', footTxt: 'text-green-800', rowAlt: 'bg-green-50/20' },
  }
  const c = corMap[cor]

  return (
    <div className="mb-8">
      <div className={`${c.header} px-4 py-2 rounded-t-lg flex items-center justify-between`}>
        <h3 className={`text-sm font-bold ${c.headerTxt} uppercase`}>
          {titulo} — {parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''}
        </h3>
        <span className={`text-sm font-bold ${c.headerTxt}`}>{fmt(total)}</span>
      </div>

      {grupos.map(([vendedor, ps], gi) => {
        const subtotal = ps.reduce((s, p) => s + Number(p.valor || 0), 0)
        return (
          <div key={vendedor} className={gi > 0 ? 'mt-3' : ''}>
            <div className="bg-green-50 px-3 py-1.5 border-x border-green-200 flex items-center justify-between">
              <span className="text-xs font-bold text-green-800 uppercase tracking-wide">{vendedor}</span>
              <span className="text-xs font-semibold text-green-700">{fmt(subtotal)}</span>
            </div>
            <div className="overflow-x-auto">
            <table className={`w-full text-left border ${c.border}`}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600">Vencimento</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600">Lote</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600">Comprador</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-center">Parcela</th>
                  {colunaExtra && <th className="px-3 py-2 text-xs font-semibold text-gray-600">{colunaExtra}</th>}
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {ps.slice().sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || '')).map((p, i) => (
                  <tr key={p.id} className={i % 2 === 0 ? 'bg-white' : c.rowAlt}>
                    <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(p.data_vencimento)}</td>
                    <td className="px-3 py-2 text-xs text-gray-700 font-mono">{p.lote?.numero_lote ? `#${p.lote.numero_lote}` : `ID ${p.lote_id}`}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{p.comprador?.nome || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 text-center">{p.numero_parcela}/{p.total_parcelas}</td>
                    {colunaExtra && <td className="px-3 py-2 text-xs text-green-700">{p.data_repasse ? fmtDate(p.data_repasse) : '—'}</td>}
                    <td className="px-3 py-2 text-xs font-semibold text-right text-gray-800">{fmt(p.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )
      })}

      <div className={`${c.foot} border border-t-0 ${c.border} px-3 py-2 flex justify-between`}>
        <span className="text-xs font-bold text-gray-700">Total {titulo}:</span>
        <span className={`text-xs font-bold ${c.footTxt}`}>{fmt(total)}</span>
      </div>
    </div>
  )
}

export default function RelatorioRepassesVendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const iframeRef = useRef(null)

  const [leiloes, setLeiloes] = useState([])
  const [loadingL, setLoadingL] = useState(true)
  const [leilaoId, setLeilaoId] = useState('')
  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('')

  const [parcelas, setParcelas] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(() => {})
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const filtros = { status: 'pago' } // Só parcelas onde comprador já pagou
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      const res = await gestaoService.listarParcelasNegociacao(orgId, filtros)
      let data = res.data || res
      if (statusFiltro) data = data.filter(p => p.status_repasse === statusFiltro)
      setParcelas(data)
    } catch {
      toast.error('Erro ao carregar parcelas')
    } finally {
      setLoading(false)
    }
  }, [orgId, modoPeriodo, leilaoId, dataInicio, dataFim, statusFiltro])

  useEffect(() => { if (orgId && (leilaoId || modoPeriodo)) carregar() }, [orgId, leilaoId]) // eslint-disable-line

  const pendente = parcelas.filter(p => p.status_repasse !== 'pago')
  const repassado = parcelas.filter(p => p.status_repasse === 'pago')
  const totalGeral = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalPendente = pendente.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalRepassado = repassado.reduce((s, p) => s + Number(p.valor || 0), 0)

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const filtroInfo = modoPeriodo
    ? `Período: ${dataInicio ? fmtDate(dataInicio) : '...'} a ${dataFim ? fmtDate(dataFim) : '...'}`
    : leilaoAtual ? leilaoAtual.nome : 'Todos os leilões'

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      if (statusFiltro) filtros.status_repasse = statusFiltro
      const res = await gestaoService.relatorioRepassesVendedorPdf(orgId, filtros)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o relatório.
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="no-print flex items-center gap-2">
        <Send className="w-5 h-5 text-green-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relatório de Repasses ao Vendedor</h1>
      </div>

      {/* Filtros */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            <button onClick={() => setModoPeriodo(false)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Por Leilão</button>
            <button onClick={() => setModoPeriodo(true)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Por Período</button>
          </div>

          {!modoPeriodo ? (
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="text-xs text-gray-500 font-medium">Leilão</label>
              <div className="relative">
                <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                  className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                  <option value="">Todos os leilões</option>
                  {leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1"><label className="text-xs text-gray-500 font-medium">De</label>
                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div className="flex flex-col gap-1"><label className="text-xs text-gray-500 font-medium">Até</label>
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Status Repasse</label>
            <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Todos</option>
              <option value="em_aberto">Pendente</option>
              <option value="pago">Repassado</option>
            </select>
          </div>

          <button onClick={carregar} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Filtrar</button>

          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!parcelas.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir / PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div>
      ) : parcelas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Nenhuma parcela encontrada</div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-800 gap-4">
            <div className="w-28 flex-shrink-0">
              {organizadorAtivo?.logo_url ? <img src={organizadorAtivo.logo_url} alt="Logo" className="max-h-14 max-w-full object-contain" /> : <span className="text-xs font-bold text-gray-700">{organizadorAtivo?.name}</span>}
            </div>
            <div className="text-center flex-1">
              <h2 className="text-xl font-black uppercase tracking-wide text-gray-900">Relatório de Repasses ao Vendedor</h2>
              <p className="text-sm text-gray-600 mt-0.5">{organizadorAtivo?.name}</p>
              <p className="text-sm text-gray-500 mt-0.5">{filtroInfo}</p>
              <p className="text-xs text-gray-400 mt-0.5">Emitido em {new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}</p>
            </div>
            <div className="w-28 flex-shrink-0" />
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-xs text-blue-600 font-medium">Total Recebido</p>
              <p className="text-lg font-bold text-blue-800">{fmt(totalGeral)}</p>
              <p className="text-xs text-blue-500">{parcelas.length} parcelas</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-xs text-yellow-600 font-medium">A Repassar</p>
              <p className="text-lg font-bold text-yellow-800">{fmt(totalPendente)}</p>
              <p className="text-xs text-yellow-500">{pendente.length} parcelas</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-xs text-green-600 font-medium">Repassado</p>
              <p className="text-lg font-bold text-green-800">{fmt(totalRepassado)}</p>
              <p className="text-xs text-green-500">{repassado.length} parcelas</p>
            </div>
          </div>

          {pendente.length > 0 && <SecaoStatus titulo="Pendente de Repasse" cor="amarelo" parcelas={pendente} />}
          {repassado.length > 0 && <SecaoStatus titulo="Repassado" cor="verde" parcelas={repassado} colunaExtra="Data Repasse" />}

          <div className="mt-6 pt-4 border-t-2 border-gray-800 flex flex-wrap justify-between gap-4 text-sm">
            <div className="flex flex-wrap gap-6">
              {pendente.length > 0 && <span className="text-yellow-700 font-semibold">A Repassar: {fmt(totalPendente)}</span>}
              {repassado.length > 0 && <span className="text-green-700 font-semibold">Repassado: {fmt(totalRepassado)}</span>}
            </div>
            <span className="font-black text-gray-900">Total: {fmt(totalGeral)}</span>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relatório de Repasses ao Vendedor</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, 'Relatorio_Repasses_Vendedor.pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Download className="w-4 h-4" /> Salvar PDF</button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir</button>
                <button onClick={closePdfModal} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Relatório Repasses" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
